import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Envelope, ArrowLeft, Copy, CheckCircle } from "@phosphor-icons/react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setResult(data);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setLoading(false);
    }
  };

  const resetHref = result?.reset_url
    ? `${window.location.origin}${result.reset_url}`
    : null;

  const copyLink = async () => {
    if (!resetHref) return;
    try {
      await navigator.clipboard.writeText(resetHref);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.warn("[ForgotPassword] request failed:", err);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12 bg-bone-100" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <Link to="/auth" className="inline-flex items-center gap-2 text-ink-500 hover:text-ink-900 mb-8 text-sm" data-testid="back-to-auth">
          <ArrowLeft size={16} /> Back to sign in
        </Link>
        <p className="eyebrow mb-3">Forgot your password?</p>
        <h1 className="font-serif text-4xl leading-tight mb-3">Send me a reset link</h1>
        <p className="text-ink-500 mb-8">
          Enter the email tied to your journal. If it's on file, we'll generate a one-hour reset link.
        </p>

        {!result ? (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="eyebrow block mb-2">Email</label>
              <div className="relative">
                <Envelope size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-500" />
                <input
                  data-testid="forgot-email-input"
                  type="email"
                  className="input pl-11"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </div>
            {err && (
              <div className="text-sm text-brand bg-brand/10 border border-brand/20 rounded-md px-3 py-2" data-testid="forgot-error">
                {err}
              </div>
            )}
            <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="forgot-submit-btn">
              {loading ? "Sending…" : "Send reset link"}
            </button>
          </form>
        ) : (
          <div className="card p-6" data-testid="forgot-success">
            <div className="flex items-start gap-3 mb-4">
              <CheckCircle size={22} weight="fill" className="text-moss shrink-0 mt-0.5" />
              <div>
                <p className="font-serif text-lg leading-snug">
                  {result.email_sent
                    ? <>We've emailed a reset link to <span className="italic">{email}</span>.</>
                    : <>If an account exists for <span className="italic">{email}</span>, a reset link has been generated.</>}
                </p>
                <p className="text-sm text-ink-500 mt-1">
                  {result.email_sent ? "Check your inbox (and spam). Link valid for one hour." : "Valid for one hour."}
                </p>
              </div>
            </div>

            {resetHref && (
              <div className="mt-5 pt-5 border-t border-bone-300">
                <p className="eyebrow mb-2">Dev mode — copy your link</p>
                <p className="text-xs text-ink-500 mb-3">
                  Email delivery isn't wired yet, so you can use the link directly.
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs bg-bone-200 border border-bone-300 rounded-md px-3 py-2 overflow-x-auto whitespace-nowrap" data-testid="reset-link-display">
                    {resetHref}
                  </code>
                  <button
                    onClick={copyLink}
                    className="p-2 rounded-md border border-bone-300 hover:bg-bone-200"
                    data-testid="copy-reset-link-btn"
                    aria-label="Copy link"
                  >
                    {copied ? <CheckCircle size={16} weight="fill" className="text-moss" /> : <Copy size={16} />}
                  </button>
                </div>
                <Link to={result.reset_url} className="btn-primary mt-5 inline-block" data-testid="open-reset-link">
                  Open reset page
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
